import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Image, Settings, ChevronUp, ChevronDown, Loader2, CheckCircle2 } from 'lucide-react';
import type { SiteNotesData } from '../utils/fileUtils';
import type { User } from '@supabase/supabase-js';
import noorLogo from '../assets/NOOR.png';
import { useSubscription } from '../hooks/useSubscription';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (exportType: 'comprehensive' | 'floorplan-only', format: 'pdf' | 'png', options?: { logoDataUrl?: string; headerColor?: string; companyName?: string; companyWebsite?: string; companyPhone?: string; companyEmail?: string; companyAddress?: string }) => void;
  siteNotesData?: SiteNotesData;
  currentUser?: User | null;
  isEstate?: boolean;
}

export default function ExportModal({ isOpen, onClose, onExport, siteNotesData, currentUser, isEstate = false }: ExportModalProps) {
  // Check subscription status
  const { isStandardPlan } = useSubscription();
  
  // Export state
  // Removed floor_plan_only option - only comprehensive exports allowed
  const [exportType] = useState<'comprehensive'>('comprehensive');
  // Removed PNG option - only PDF exports allowed
  const [exportFormat] = useState<'pdf'>('pdf');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>('');  const [exportSuccess, setExportSuccess] = useState(false);
  
  // Customization state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = useState<boolean>(false);
  const [headerColor, setHeaderColor] = useState<string>('#f59e0b');
  
  // Company details customization
  const [companyName, setCompanyName] = useState<string>('Energy Ltd');
  const [companyWebsite, setCompanyWebsite] = useState<string>('www.example.com');
  const [companyPhone, setCompanyPhone] = useState<string>('0800 123 4567');
  const [companyEmail, setCompanyEmail] = useState<string>('info@example.com');
  const [companyAddress, setCompanyAddress] = useState<string>('123 Business Street, London, EC2A 4DP');
  
  // Profile management
  interface CustomizationProfile {
    id: string;
    name: string;
    logoDataUrl: string | null;
    headerColor: string;
    companyName: string;
    companyWebsite: string;
    companyPhone: string;
    companyEmail: string;
    companyAddress: string;
    createdAt: number;
  }
  
  const [savedProfiles, setSavedProfiles] = useState<CustomizationProfile[]>([]);
  const [profileName, setProfileName] = useState<string>('');
  const [showSaveProfile, setShowSaveProfile] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [tempColor, setTempColor] = useState(headerColor);
  
  // Step navigation
  const [currentStep, setCurrentStep] = useState<'customization' | 'export'>('customization');

  // Load saved profiles from localStorage
  useEffect(() => {
    const loadedProfiles = localStorage.getItem('floorplan-export-profiles');
    if (loadedProfiles) {
      try {
        setSavedProfiles(JSON.parse(loadedProfiles));
      } catch (error) {
        console.error('Failed to load profiles:', error);
      }
    }
  }, []);

  // Load default logo on mount
  useEffect(() => {
    const loadDefaultLogo = async () => {
      try {
        const response = await fetch(noorLogo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setLogoPreview(e.target.result as string);
          }
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.log('Could not load default logo:', error);
      }
    };
    
    if (isOpen && !logoPreview && !logoRemoved) {
      loadDefaultLogo();
    }
  }, [isOpen, logoPreview, logoRemoved]);

  // Handlers
  const goToNextStep = () => {
    setCurrentStep('export');
  };

  const goBackToCustomization = () => {
    setCurrentStep('customization');
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportSuccess(false);
    setExportProgress('Preparing export...');
    
    try {
      // Standard plan users: NOOR logo only, no contact info
      // Pro/Business users: Full customization
      const options = { 
        logoDataUrl: logoPreview || undefined,
        headerColor: headerColor,
        companyName: isStandardPlan ? '' : companyName,
        companyWebsite: isStandardPlan ? '' : companyWebsite,
        companyPhone: isStandardPlan ? '' : companyPhone,
        companyEmail: isStandardPlan ? '' : companyEmail,
        companyAddress: isStandardPlan ? '' : companyAddress
      };
      // Always export as comprehensive
      const mappedType = 'comprehensive' as const;
      
      setExportProgress('Generating floor plan...');
      await new Promise(resolve => setTimeout(resolve, 300)); // Brief delay for UI feedback
      
      setExportProgress('Creating PDF document...');
      await onExport(mappedType, exportFormat, options);
      
      setExportProgress('Export complete!');
      setExportSuccess(true);
      
      // Auto-close modal after success
      setTimeout(() => {
        onClose();
        setExportSuccess(false);
        setExportProgress('');
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      setExportProgress('Export failed. Please try again.');
      setTimeout(() => {
        setExportProgress('');
      }, 3000);
    } finally {
      setTimeout(() => {
        setIsExporting(false);
      }, 2000);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          const logoDataUrl = e.target.result as string;
          setLogoPreview(logoDataUrl);
          setLogoFile(file);
          setLogoRemoved(false); // Reset removal state when new logo uploaded
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const openColorPicker = () => {
    setTempColor(headerColor);
    setShowColorPicker(true);
  };

  const handleColorChange = (newColor: string) => {
    setTempColor(newColor);
  };

  const applyColor = () => {
    setHeaderColor(tempColor);
    setShowColorPicker(false);
  };

  const cancelColorChange = () => {
    setTempColor(headerColor);
    setShowColorPicker(false);
  };


  
  // Profile Management
  const saveProfile = () => {
    if (!profileName.trim()) {
      alert('Please enter a profile name');
      return;
    }
    
    const newProfile: CustomizationProfile = {
      id: Date.now().toString(),
      name: profileName.trim(),
      logoDataUrl: logoPreview,
      headerColor,
      companyName,
      companyWebsite,
      companyPhone,
      companyEmail,
      companyAddress,
      createdAt: Date.now()
    };
    
    const updatedProfiles = [...savedProfiles, newProfile];
    setSavedProfiles(updatedProfiles);
    localStorage.setItem('floorplan-export-profiles', JSON.stringify(updatedProfiles));
    setProfileName('');
    setShowSaveProfile(false);
  };
  
  const loadProfile = (profile: CustomizationProfile) => {
    setLogoPreview(profile.logoDataUrl);
    setHeaderColor(profile.headerColor);
    setCompanyName(profile.companyName);
    setCompanyWebsite(profile.companyWebsite);
    setCompanyPhone(profile.companyPhone);
    setCompanyEmail(profile.companyEmail || 'info@example.com');
    setCompanyAddress(profile.companyAddress || '123 Business Street, London, EC2A 4DP');
    
    // Clear the file input since we're loading from saved data
    setLogoFile(null);
    const fileInput = document.getElementById('logo-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };
  
  const deleteProfile = (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;
    
    const updatedProfiles = savedProfiles.filter(p => p.id !== profileId);
    setSavedProfiles(updatedProfiles);
    localStorage.setItem('floorplan-export-profiles', JSON.stringify(updatedProfiles));
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemoved(true);
    const fileInput = document.getElementById('logo-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Export Floor Plan</h2>
                <p className="text-blue-100 text-sm">Choose your export format and type</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
          
          {/* Step Progress Indicator */}
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className={`flex items-center space-x-2 ${currentStep === 'customization' ? 'text-blue-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'customization' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
              }`}>
                {currentStep === 'export' ? '✓' : '1'}
              </div>
              <span className="text-sm font-medium">Customize</span>
            </div>
            <div className={`w-12 h-0.5 ${currentStep === 'export' ? 'bg-green-200' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center space-x-2 ${currentStep === 'export' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'export' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
              }`}>
                2
              </div>
              <span className="text-sm font-medium">Export</span>
            </div>
          </div>

          {/* Step 1: Customization */}
          {currentStep === 'customization' && isStandardPlan && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-amber-800">
                  <Settings className="w-4 h-4" />
                  <p className="text-sm font-medium">Customization is not available on the Standard plan</p>
                </div>
                <p className="text-xs text-amber-700 mt-2">Upgrade to Pro to customize logos, colors, and company details in your exports.</p>
              </div>
              
              {/* Next Button for Standard Users */}
              <div className="pt-2">
                <button
                  onClick={goToNextStep}
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <span>Next: Export Options</span>
                  <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </button>
              </div>
            </>
          )}
          
          {currentStep === 'customization' && !isStandardPlan && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <Settings className="w-4 h-4 text-gray-600" />
                  <h3 className="text-sm font-semibold text-gray-900">Customization</h3>
                  {(logoFile || headerColor !== '#f59e0b') && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
                      Active
                    </span>
                  )}
                </div>
              </div>

              <div className="p-4">
                <div className="space-y-4">
                  {/* Saved Profiles */}
                  {savedProfiles.length > 0 && (
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-700">Saved Profiles</label>
                        <span className="text-[10px] text-gray-500">{savedProfiles.length} saved</span>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {savedProfiles.map(profile => (
                          <div key={profile.id} className="flex items-center space-x-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                            {profile.logoDataUrl && (
                              <img src={profile.logoDataUrl} alt="" className="h-6 w-auto object-contain" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-900 truncate">{profile.name}</div>
                              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: profile.headerColor }}></div>
                                <span className="truncate">{profile.companyName}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => loadProfile(profile)}
                              className="px-2 py-1 text-[10px] font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => deleteProfile(profile.id)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Save Current Profile */}
                  {!showSaveProfile ? (
                    <button
                      onClick={() => setShowSaveProfile(true)}
                      className="w-full px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Current Configuration as Profile
                    </button>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        placeholder="Enter profile name..."
                        className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => e.key === 'Enter' && saveProfile()}
                        autoFocus
                      />
                      <button
                        onClick={saveProfile}
                        className="px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setShowSaveProfile(false); setProfileName(''); }}
                        className="px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  
                  {/* Logo Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-700">Logo</label>
                      {logoFile && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">Custom</span>
                      )}
                    </div>
                    
                    {logoPreview ? (
                      <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded border">
                        <img src={logoPreview} alt="Logo" className="h-8 w-auto object-contain" />
                        <div className="flex-1 text-xs text-gray-500 truncate">
                          {logoFile ? logoFile.name : 'NOOR logo'}
                        </div>
                        <button
                          onClick={handleRemoveLogo}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove logo"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="cursor-pointer text-xs text-gray-500 hover:text-gray-700"
                        >
                          Click to upload logo
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Theme Color Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-700">Theme Color</label>
                      {headerColor !== '#f59e0b' && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">Custom</span>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <button
                        onClick={openColorPicker}
                        className="flex items-center space-x-2 w-full px-3 py-2 text-left border border-gray-300 rounded hover:border-gray-400 transition-colors"
                      >
                        <div 
                          className="w-5 h-5 rounded border border-gray-300 shadow-inner"
                          style={{ backgroundColor: headerColor }}
                        ></div>
                        <span className="text-sm font-mono">{headerColor}</span>
                        <span className="text-xs text-gray-500 ml-auto">Click to change</span>
                      </button>
                      <div className="text-[10px] text-gray-500 pl-1">
                        Applied to headers, borders & accent elements
                      </div>
                    </div>

                    {/* Color Picker Modal */}
                    {showColorPicker && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
                          <h3 className="text-lg font-semibold mb-4">Choose Color</h3>
                          
                          <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                              <input
                                type="color"
                                value={tempColor}
                                onChange={(e) => handleColorChange(e.target.value)}
                                className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                              />
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={tempColor}
                                  onChange={(e) => handleColorChange(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                                  placeholder="#f59e0b"
                                />
                              </div>
                            </div>
                            
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                              <button
                                onClick={cancelColorChange}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={applyColor}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Company Details */}
                  <div className="space-y-3 pt-3 border-t border-gray-100">
                    <div className="text-xs font-medium text-gray-700 mb-2">Company Details</div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Company Name</label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Energy Ltd"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Website</label>
                      <input
                        type="text"
                        value={companyWebsite}
                        onChange={(e) => setCompanyWebsite(e.target.value)}
                        placeholder="www.yourcompany.com"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Phone Number</label>
                      <input
                        type="text"
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(e.target.value)}
                        placeholder="0800 123 4567"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Email Address</label>
                      <input
                        type="email"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        placeholder="info@yourcompany.com"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] text-gray-600 mb-1 block">Address</label>
                      <input
                        type="text"
                        value={companyAddress}
                        onChange={(e) => setCompanyAddress(e.target.value)}
                        placeholder="123 Business Street, City, Postcode"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Next Button */}
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      onClick={goToNextStep}
                      className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <span>Next: Export Options</span>
                      <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Export Options */}
          {currentStep === 'export' && (
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Download className="w-4 h-4 text-gray-600" />
                    <h3 className="text-sm font-semibold text-gray-900">Export Options</h3>
                  </div>
                  <button
                    onClick={goBackToCustomization}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                  >
                    <ChevronDown className="w-3 h-3 rotate-90" />
                    <span>Back</span>
                  </button>
                </div>
              </div>

              <div className="p-4">
                <div className="space-y-4">
                  {/* Export Info - Fixed to Comprehensive PDF */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="text-sm font-semibold text-blue-900">Comprehensive PDF Export</div>
                        <div className="text-xs text-blue-700">Full assessment data with floor plans, calculations, and site notes</div>
                      </div>
                    </div>
                  </div>

                  {/* Export Button with Progress */}
                  <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="w-full py-3 px-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isExporting ? (
                      <>
                        {exportSuccess ? (
                          <CheckCircle2 className="w-5 h-5 animate-pulse" />
                        ) : (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        )}
                        <span>{exportProgress || 'Exporting...'}</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        <span>Export {exportFormat.toUpperCase()}</span>
                      </>
                    )}
                  </button>
                  
                  {/* Progress Status Message */}
                  {isExporting && exportProgress && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        {exportSuccess ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        )}
                        <span className={`text-sm font-medium ${
                          exportSuccess ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {exportProgress}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
